import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBookmark as faBookmarkOutline } from '@fortawesome/free-regular-svg-icons';
import { faBookmark as faBookmarkFilled } from '@fortawesome/free-solid-svg-icons';
import { finalize, Observable } from 'rxjs';

import { catalogSkuShopUrl, CatalogSku, isAvailableAtSource } from 'src/app/common/CatalogSku';
import { contentLanguage } from 'src/app/common/content-language';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { ImageUrl } from 'src/app/common/ImageUrl';
import { AsyncClickDirective } from 'src/app/directives/async-click.directive';
import { PriceNotePipe } from 'src/app/pipes/price-note.pipe';
import { CallApiService } from 'src/app/services/api-action.service';
import { BookmarkService } from 'src/app/services/bookmark.service';
import { CatalogService } from 'src/app/services/catalog.service';
import { PersonalizationService } from 'src/app/services/personalization.service';
import { AppProfileService } from 'src/app/services/profile.service';

import { AddToCartComponent } from '../add-to-cart/add-to-cart.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

/**
 * Detail page for a single catalog listing, reached by clicking a card in the
 * search results. Shows every image of the sku, the title, price, description,
 * seller and availability, plus the order action.
 *
 * Public like `/search` itself, so an anonymous visitor may browse here. The
 * template therefore gates `app-add-to-cart` behind `profile?.customer`, so
 * nobody is offered an order action they cannot perform.
 */
@Component({
  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    AddToCartComponent,
    AsyncClickDirective,
    LoadingSpinnerComponent,
    PriceNotePipe,
  ],
  selector: 'app-catalog-item-detail',
  templateUrl: './catalog-item-detail.component.html',
  styleUrls: ['./catalog-item-detail.component.css'],
})
export class CatalogItemDetailComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private catalogService = inject(CatalogService);
  private apiActionService = inject(CallApiService);
  private profileService = inject(AppProfileService);
  private bookmarkService = inject(BookmarkService);
  private personalizationService = inject(PersonalizationService);
  private location = inject(Location);

  private destroyRef = inject(DestroyRef);

  public catalogSku: CatalogSku = null;
  public profile: ExtendedProfile = null;

  // #652: the bookmarked listing ids — one shared set with the results grid, so opening a
  // card that was already bookmarked shows it marked without another request.
  private bookmarkedIds: ReadonlySet<string> = new Set<string>();

  /**
   * Starts true so the first render shows the spinner rather than the
   * "not found" panel while the request is still in flight.
   */
  public loading = true;

  /** #94: one page view records at most one visit — see {@link recordVisit}. */
  private visitRecorded = false;

  ngOnInit(): void {
    const skuId = this.activatedRoute.snapshot.paramMap.get('id');

    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      this.recordVisit(skuId);
    });

    this.bookmarkService.ensureIdsLoaded();
    this.bookmarkService.bookmarkedIds$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => (this.bookmarkedIds = ids));

    // The backend's GET /catalog/{id} re-runs the list filter and returns
    // FirstOrDefault(), so an unknown, inactive or out-of-stock id comes back
    // either as an empty body (200) or as a 500 from the controller's unguarded
    // model access — never a 404. Both land on the "not found" panel: the empty
    // body leaves catalogSku null, the 500 additionally raises the error toast.
    this.apiActionService.callWithToasts(
      this.catalogService.getById(skuId).pipe(finalize(() => (this.loading = false))),
      null,
      $localize`:@@catalogItemDetail.loadError:Der Artikel konnte nicht geladen werden.`,
      (sku: CatalogSku) => (this.catalogSku = sku)
    );
  }

  /**
   * #94: records that this listing was opened — the "Zuletzt angesehen" strip on the landing
   * page is built from these. Fire-and-forget and deliberately silent: a signal that failed
   * to record must never put an error toast on a page the user came here to read.
   *
   * Only for a signed-in user, because this route is public and the recorder is `[Authorize]`.
   * No branch on the consent setting though — the backend answers 204 while personalization
   * is off and discards what it must not keep (#651), so the client needs none.
   *
   * `currentProfile$` re-emits on every profile refresh; the flag keeps one page view to one
   * recorded visit (the endpoint is idempotent, but a repeat is still a wasted request).
   */
  private recordVisit(skuId: string): void {
    if (this.visitRecorded || !skuId || !this.profile?.id) {
      return;
    }
    this.visitRecorded = true;
    this.personalizationService.recordVisit(skuId).subscribe({ error: () => {} });
  }

  /**
   * Every image of the listing. The backend emits exactly one ImageUrl entry
   * per file (carrying the three size variants), so the images are spread
   * across `files`, not across `files[0].imageUrls`.
   */
  get images(): ImageUrl[] {
    return (this.catalogSku?.files ?? []).map((f) => f?.imageUrls?.[0]).filter((u) => !!u);
  }

  /** Same wording as the seller info page and "Meine Daten" (#103). */
  get sellerLabel(): string {
    return this.catalogSku?.isRetailer
      ? $localize`:@@catalogItemDetail.seller.retailer:Gewerblicher Anbieter`
      : $localize`:@@catalogItemDetail.seller.private:Privater Anbieter`;
  }

  /**
   * The listing's availability state, verbatim. Backend #562 (decision 3) makes this the
   * field that suppresses the order action, so it is deliberately NOT defaulted to
   * 'available': an absent or unrecognised state renders the neutral badge and no order
   * action rather than an offer the checkout gate would reject.
   */
  get availability(): string {
    return this.catalogSku?.availability;
  }

  /**
   * backend #690: the language this listing's own text is written in, or null when the source
   * declared none. Null renders no `lang` attribute at all — see `contentLanguage`.
   */
  get contentLanguage(): string | null {
    return contentLanguage(this.catalogSku?.skuLanguage);
  }

  /**
   * Backend #562 (F1): „Beim Anbieter erhältlich" — the seller has it, we cannot sell it.
   * Accepts both spellings of the state while the backend rename is rolling out; an absent or
   * unrecognised state is false here and lands on the neutral badge, as before.
   */
  get isAvailableAtSource(): boolean {
    return isAvailableAtSource(this.availability);
  }

  /**
   * Backend #562: the source shop's product page for an imported listing, or null. Drives the
   * „Zum Shop" link that replaces the order action — see `catalogSkuShopUrl` for why an
   * unusable URL deliberately yields no link at all.
   */
  get shopUrl(): string | null {
    return catalogSkuShopUrl(this.catalogSku);
  }

  /**
   * #652 (decision 3a): the Merkliste API is `[Authorize]`, so this public page hides the
   * bookmark button from an anonymous visitor rather than offering one that 401s.
   */
  get canBookmark(): boolean {
    return !!this.profile?.id;
  }

  get isBookmarked(): boolean {
    return this.bookmarkedIds.has(this.catalogSku?.skuId);
  }

  /** Solid while bookmarked, outlined while not — the state is visible without colour. */
  get bookmarkIcon() {
    return this.isBookmarked ? faBookmarkFilled : faBookmarkOutline;
  }

  /** The button carries no text; its accessible name has to carry both states. */
  get bookmarkLabel(): string {
    return this.isBookmarked
      ? $localize`:@@catalog.bookmark.remove:Nicht mehr merken`
      : $localize`:@@catalog.bookmark.add:Merken`;
  }

  /**
   * `[asyncClick]` factory — the service flips its id set before the request goes out and
   * puts it back if the request fails; the toast makes that rollback visible.
   */
  bookmarkAction(catalogSkuId: string): () => Observable<unknown> {
    return () =>
      this.apiActionService.callWithToasts$(
        this.bookmarkService.toggle(catalogSkuId),
        null,
        $localize`:@@catalog.bookmark.error:Die Merkliste konnte nicht geändert werden.`
      );
  }

  /**
   * Image shown in the fullscreen lightbox. Angular owns this index outright —
   * the modal renders a plain <img> rather than a second Bootstrap carousel, so
   * nothing else mutates the visible slide behind its back.
   */
  public lightboxIndex = 0;

  openLightbox(index: number): void {
    this.lightboxIndex = index;
  }

  showPreviousImage(): void {
    this.step(-1);
  }

  showNextImage(): void {
    this.step(1);
  }

  /** Wraps around at both ends, so the arrows never dead-end. */
  private step(delta: number): void {
    const count = this.images.length;
    if (count > 1) {
      this.lightboxIndex = (this.lightboxIndex + delta + count) % count;
    }
  }

  onBackClicked(): void {
    this.location.back();
  }
}
